'use client'

import { getCookie } from 'cookies-next';
import { Product } from 'lib/bigcommerce/types';
import { Customer } from 'lib/bigcommerce/types/customer';
import { CUSTOM_ANALYTICS_URL } from 'lib/constants';
import { BCLineItem, BCOrder } from 'lib/rest-service/types';
import { extractConsignments, extractLineItems, isDevelopment, isSB, tryParseMoney } from 'lib/utils';
import * as deviceDetect from 'react-device-detect';
import { CustomAnalytics, FunnelData, cleanQueryParams, type State } from 'shared-react-components';

const baseData = async (): Promise<State> => {
  if (typeof window === 'undefined') return {};

  const device = getDevice();
  const baseData:State = {
    device: {
      name: device,
      manufacturer: deviceDetect.isMobile ? deviceDetect.mobileVendor : deviceDetect.browserName,
      type: deviceDetect.isMobile ? deviceDetect.deviceType : 'desktop',
      resolution: {
        width: window.screen.availWidth,
        height: window.screen.availHeight
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      encoding: 'UTF-8',
      language: window.navigator.language,
      colors: `${window.screen.colorDepth}-bit`
    },
    browser: {
      manufacturer: device,
      name: deviceDetect.browserName,
      majorVersion: deviceDetect.browserVersion,
      version: deviceDetect.fullBrowserVersion,
      window: {
        width: window.screen.availWidth,
        height: window.screen.availHeight
      }
    },
    os: {
      majorVersion: deviceDetect.osName,
      name: deviceDetect.osName,
      version: deviceDetect.osVersion
    },
    scrollPosition: window.scrollY.toFixed(),
    ipAddress: await getIPAddress()
  };
  return baseData;
};

type PageViewProps = {
  customer?: Customer;
  sessionId?: string;
  funnelData?: FunnelData;
};
export const pageEnter = async (props: PageViewProps) => {
  if (typeof window === 'undefined') return {};

  const { customer, sessionId, funnelData } = props;
  const queryParams = cleanQueryParams(window.location.search);

  const customAnalytics = new CustomAnalytics(CUSTOM_ANALYTICS_URL, isDevelopment() || isSB());
  const baseDataResolved:State = await baseData()
  const pageEnterEvent = {
    sessionId,
    test: {
      testId: undefined,
      variantId: undefined,
      testIdVariantId: undefined,
      testName: undefined,
      variantName: undefined
    },
    page: {
      url: {
        raw: window.location.href,
        base: window.location.origin + window.location.pathname,
        queryParams: window.location.search
      },
      queryParams: Object.fromEntries(queryParams),
    },
    ...baseDataResolved,
    referrer: window.document.referrer,
    affiliate: {
      id: queryParams.get('afid'),
      campaignId: queryParams.get('aff_sub') ?? queryParams.get('source')
    },
    platform: {
      id: !!funnelData?.productLine ? 'funnel' : 'storefront',
      funnelData: {
        brand: funnelData?.brand,
        productLine: funnelData?.productLine,
        funnelName: funnelData?.funnelName,
        stepName: funnelData?.stepName,
        type: funnelData?.type,
      }
    },
    ...(customer ? {
      customer: {
        id: customer.entityId,
        email: customer.email,
        associatedAccountEmail: customer?.associatedAccountEmail,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone
      }
    } : {}),
  };
  return await customAnalytics.pageEnter(pageEnterEvent);
};

export const pageLeave = async (_props: PageViewProps) => {
  if (typeof window === 'undefined') return {};

  const customAnalytics = new CustomAnalytics(CUSTOM_ANALYTICS_URL, isDevelopment() || isSB());
  const pageLeaveEvent = await baseData();
  return await customAnalytics.pageLeave(pageLeaveEvent);
};


type PurchaseProps = {
  orderId: number;
  customer?: Customer;
  sessionId?: string;
  order?: BCOrder;
  product?: Product;
  funnelData?: FunnelData;
};

export const purchase = async (props: PurchaseProps) => {
  if (typeof window === 'undefined') return {};

  const { customer, orderId, order, product, funnelData } = props;

  const sessionId = getCookie('sessionId')?.toString();
  const customAnalytics = new CustomAnalytics(CUSTOM_ANALYTICS_URL, isDevelopment() || isSB());
  const queryParams = cleanQueryParams(window.location.search);
  const baseDataResolved:State = await baseData();


  const { shipping, downloads } = extractConsignments(order?.consignments);
  const shippingLineItems = extractLineItems(shipping);
  const downloadLineItems = extractLineItems(downloads);

  const lineItem = shippingLineItems?.at(0) ?? downloadLineItems?.at(0);
  const variant = product?.variants?.find(v => lineItem?.sku === v.sku);
  const gpm = variant?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString() ?? product?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString();
  const gpmPrice = tryParseMoney(gpm) ?? 0;
  const quantity = lineItem?.quantity ?? 1;
  const gpmTotal = gpmPrice * quantity;

  const billingAddress = {
    email: order?.billing_address?.email,
    phone: order?.billing_address?.phone,
    address: order?.billing_address?.street_1,
    city: order?.billing_address?.city,
    country: order?.billing_address?.country_iso2,
    state: order?.billing_address?.state,
    zip: order?.billing_address?.zip,
  };

  const consignment = shipping?.at(0);
  const shippingAddress = {
    email: consignment?.email,
    phone: consignment?.phone,
    address: consignment?.street_1,
    city: consignment?.city,
    country: consignment?.country_iso2,
    state: consignment?.state,
    zip: consignment?.zip,
  };

  return await customAnalytics.purchase({
    sessionId,
    test: {
      testId: undefined,
      variantId: undefined,
      testIdVariantId: undefined,
      testName: undefined,
      variantName: undefined
    },
    page: {
      url: {
        raw: window.location.href,
        base: window.location.origin + window.location.pathname,
        queryParams: window.location.search
      },
      queryParams: Object.fromEntries(queryParams),
    },
    ...baseDataResolved,
    referrer: window.document.referrer,
    affiliate: {
      id: queryParams.get('afid'),
      campaignId: queryParams.get('aff_sub') ?? queryParams.get('source')
    },
    platform: {
      id: !!funnelData?.productLine ? 'funnel' : 'storefront',
      funnelData: {
        brand: funnelData?.brand,
        productLine: funnelData?.productLine,
        funnelName: funnelData?.funnelName,
        stepName: funnelData?.stepName,
        type: funnelData?.type,
      }
    },
    order: {
      id: order?.id ?? orderId,
      currency: order?.currency_code ?? 'USD',
      channel_id: !!funnelData?.productLine ? 'funnel' : 'storefront',
      subtotal: stringToFixed(order?.subtotal_ex_tax),
      grandTotal: stringToFixed(order?.total_inc_tax),
      shipping: stringToFixed(order?.shipping_cost_inc_tax),
      tax: stringToFixed(order?.total_tax),
      promoCode: undefined,
      gpmTotal: gpmTotal?.toFixed(2),
      items: [customOrderLineItem({ lineItem, product })],
    },
    customer: {
      id: customer?.entityId,
      email: customer?.email ?? billingAddress.email ?? shippingAddress.email,
      associatedAccountEmail: customer?.associatedAccountEmail,
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      phone: customer?.phone,
      billing: billingAddress,
      shipping: shippingAddress,
    },
  });
}

// Mapper Functions

type OrderLineItemProps = { lineItem?: BCLineItem, product?: Product };
const customOrderLineItem = ({ lineItem, product }: OrderLineItemProps) => {
  if (!lineItem) {
    return {};
  }

  const variant = product?.variants?.find(v => lineItem.sku === v.sku);
  const gpm = variant?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString() ?? product?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString();
  const selectedOptions = lineItem.product_options?.map(({ name, value }) => `${name}: ${value}`)?.join(' ');
  const discountAmount = lineItem.applied_discounts?.reduce(
    (total, discount) => {
      return total + (tryParseMoney(discount.amount) ?? 0);
    }, 0
  );
  return {
    id: lineItem.sku,
    name: lineItem.name,
    selected_options: selectedOptions,
    brand: product?.brand,
    category: product?.categories?.at(0)?.name,
    price: stringToFixed(lineItem.price_ex_tax),
    retail: stringToFixed(lineItem.base_price),
    discount: discountAmount?.toFixed(2),
    gpm: stringToFixed(gpm),
    quantity: lineItem.quantity
  };
};

// Utility Functions

const stringToFixed = (money?: string) => {
  return tryParseMoney(money)?.toFixed(2)
};

export function getDevice() {
  if (deviceDetect.isAndroid) return 'Android'
  if (deviceDetect.isWinPhone) return 'Windows Phone'
  if (deviceDetect.isMobileSafari) return 'Safari'
  if (deviceDetect.isIOS) return 'iOS'
  if (deviceDetect.isChrome) return 'Chrome'
  if (deviceDetect.isFirefox) return 'Firefox browser'
  if (deviceDetect.isSafari) return 'Safari browser'
  if (deviceDetect.isOpera) return 'Opera browser'
  if (deviceDetect.isIE) return 'Internet Explorer browser'
  if (deviceDetect.isEdge) return 'Edge browser'
  if (deviceDetect.isChromium) return 'Chromium'
  if (deviceDetect.isSmartTV) return 'SmartTv'
}
export async function getIPAddress(): Promise<string> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    if (!response.ok) {
      throw new Error(`Failed to fetch IP address: ${response.statusText}`);
    }
    const data = await response.json();
    return data?.ip || '';
  } catch (error) {
    console.error('Error fetching IP address:', error);
    return '';
  }
}
